import React, { useEffect, useMemo, useState } from "react";
import { CollectionColumn, OrderType, useCollectionBySlugQuery, useCollectionBySlugV2Query, useGetCollectionTraitsBySlugQuery } from "@graphql-generated";
import HomeLayout from "@layouts/home";
import { ContentfulService } from "@services/contentfulService";
import type { NextPage } from "next";
import { useRouter } from "next/router";
import { CFSHomeItem } from "../../../index.page";
import { useMediaQuery, useTheme } from "@mui/material";
import { CheckboxListData } from "@layouts/filterLayout";
import { getTotalPage } from "src/utils/getTotalPage.utils";
import { numericRegex } from "src/constants/regex";
import { Configuration } from "@constants";
import { saleCollection } from "src/enums/saleCollection.enum";

interface PrivacyPageProps {}

interface FilteredTrait {
  traitType: string;
  traitValue: string[];
}

const collectionsLimit = 24;

const AboutProject: NextPage<PrivacyPageProps> = ({}: PrivacyPageProps) => {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [contentData, setContentdata] = useState<any>({});
  const [openFilter, setOpenFilter] = useState<boolean>(isMobile ? false : true);
  const [offset, setOffset] = useState<number>(0);
  
  const isSlug = useMemo(() => {
    return (router?.query["slug"]?.includes(saleCollection?.MAYA_LIN_PROD) || router?.query["slug"]?.includes(saleCollection?.MAYA_LIN_SALE));
  }, [router])
  const slug = useMemo(() => router?.query?.["slug"]?.toString() || "", [router?.query])
  const marketPlaceId = useMemo(() => router?.query?.["marketPlaceId"], [router?.query]);
  const { data, loading: loadingCollection } = useCollectionBySlugQuery({
    fetchPolicy: 'network-only',
    variables: {
      slug,
      marketplaceID: marketPlaceId,
    },
    skip: !slug || !marketPlaceId || isSlug
  });

  const { data: traitsList, loading } = useGetCollectionTraitsBySlugQuery({
    variables: {
      slug: router.query["slug"]?.toString() || "",
      marketplaceID: router?.query["marketPlaceId"],
    },
    skip: !isSlug
  });
  
  const traitsFilterData: any = (traitsList?.getCollectionTraitsBySlug ?? [])?.map((item) => {
    return {
      id: item?.traitType,
      label: item?.traitType,
      type: item?.dataType,
      value: item?.traitValues?.map((res) => { return { id: res?.traitValue, label: res?.traitValue?.toString(), count: res?.count } })
    }
  });

  const initialSearchValue = Array.from({ length: traitsFilterData?.length }, () => '');
  const initialSelectedItem = traitsFilterData?.map(() => []);
  
  //Filter state
  const [selectedItems, setSelectedItems] = useState<string[][]>(initialSelectedItem);
  const [searchValue, setSearchValue] = useState<string[]>(initialSearchValue);
  const [filteredTraits, setFilteredTraits] = useState<FilteredTrait[]>([]);
  const [collectionSearchValue, setCollectionSearchValue] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState({
    value: OrderType.Asc,
    label: 'Newest'
  });
  
  useEffect(() => {
    if (initialSearchValue && !loading && initialSelectedItem) {
      setSearchValue(initialSearchValue);
      setSelectedItems(initialSelectedItem);
    }
  }, [loading]);
  
  const { data: collectionDataV2, refetch: refetchCollectionBySlugV2, loading: loadingCollectionV2 } = useCollectionBySlugV2Query({
    variables: {
      slug: router.query["slug"]?.toString() || "",
      marketplaceID: router?.query["marketPlaceId"],
      limit: collectionsLimit,
      offset: offset * collectionsLimit,
      filter: filteredTraits,
      search: collectionSearchValue,
      sort: {
        column: CollectionColumn.CreatedAt,
        type: selectedSort.value,
      }
    },
    skip: !isSlug
  });

  const totalPage = getTotalPage(collectionDataV2?.collectionBySlugV2?.items?.filteredCount || 0, collectionsLimit);

  useEffect(() => {
    if ((filteredTraits || collectionSearchValue || selectedSort)&& isSlug) {
      const delayDebounce = setTimeout(async () => {
        refetchCollectionBySlugV2();
      }, 200);
      return () => clearTimeout(delayDebounce);
    }
    return () => undefined;
  }, [filteredTraits, collectionSearchValue, selectedSort]);

  const transformData = (): FilteredTrait[] => {
    const output: FilteredTrait[] = [];
  
    traitsFilterData?.forEach((item: CheckboxListData, index: number) => {
      const selectedFilterList = selectedItems?.[index];
  
      if (selectedFilterList && selectedFilterList?.length > 0) {
        const traitType = item?.label;
        const traitValue = selectedFilterList;
  
        output?.push({ traitType, traitValue });
      }
    });
    setFilteredTraits(output);
    const totalOffset = offset * collectionsLimit;
    if (totalOffset >= collectionsLimit) {
      setOffset(0);
    }
    return output;
  };

  const cfs = useMemo(() => {
    return new ContentfulService();
  }, []);
  const [cfsItem, setCfsItem] = useState<CFSHomeItem>({});

  useEffect(() => {
    if (selectedItems ) {
      transformData();
    }
  }, [selectedItems]);

  useEffect(() => {
    getHomePageCFS();
  }, []);

  if (JSON.stringify(data) !== JSON.stringify(contentData)) {
    if (data) {
      setContentdata(data);
    }
  }
  const getHomePageCFS = () => {
    cfs
      .getHomePageItem()
      .then((res) => {
        setCfsItem(res as unknown as CFSHomeItem);
      })
  };

  const onClickClearFilter = (isMobile: boolean) => {
    setSelectedItems(initialSelectedItem);
    setSearchValue(initialSearchValue);
    setOffset(0);
    if (isMobile) {
      setOpenFilter(false);
    }
  }

  const onClickFilter = () => {
    setOpenFilter(!openFilter);
  }

  const onClickMenu = (event: OrderType, label: string) => {
    setSelectedSort({
      label: label,
      value: event,
    });
  };

  const onChangeCollectionSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCollectionSearchValue(event.target.value);
  };

  const onChangeSearch = (event: string, index: number) => {
    setSearchValue(prevValues => {
      const updatedValues = [...prevValues];
      updatedValues[index] = event;
      return updatedValues;
    });
  };

  const onChangeCheckbox = (itemId: string, index: number) => {
    setSelectedItems(prevSelectedItems => {
      const updatedItems = [...prevSelectedItems];
      const indexSelectedItems = updatedItems[index] || [];
      if (indexSelectedItems?.includes(itemId)) {
        updatedItems[index] = indexSelectedItems?.filter(id => id !== itemId);
      } else {
        updatedItems[index] = [...indexSelectedItems, itemId];
      }
      return updatedItems;
    });
  };

  const setCounterCount = (count: string, index: number) => {
    const formatCount = parseInt(count, 10);
    const checkIsValidNumber = isNaN(formatCount) ? 0 : formatCount;
    if (count.match(numericRegex)) {
      setSelectedItems((prevSelectedItems) => {
        const updatedItems = [...prevSelectedItems];
        if (updatedItems[index] === undefined) {
          updatedItems[index] = [checkIsValidNumber.toString()];
        } else {
          updatedItems[index] = [checkIsValidNumber.toString()];
        }
        return updatedItems;
      });
    }
  };

  const onClickRemoveItem = (rowIndex: number, colIndex: number) => {
    const updatedItem = [...selectedItems];
    if (updatedItem[rowIndex] && updatedItem[rowIndex][colIndex]) {
      updatedItem[rowIndex].splice(colIndex, 1);
      setSelectedItems(updatedItem);
    }
  }

  const handlePagination = (page: number) => {
    if (typeof document !== undefined) { document.documentElement.scrollTop = 0; }
    setOffset(page - 1);
  };

  const onClickDone = () => {
    setOpenFilter(false);
  }
  return (
    <HomeLayout
      data={ isSlug ? collectionDataV2?.collectionBySlugV2?.items : contentData?.collectionBySlug?.items }
      homeCfs={cfsItem}
      projectDetails={router?.query["collectionId"]}
      isSlug={ isSlug ?? false }
      traitsData={ traitsFilterData }
      searchValue={ searchValue }
      selectedItems={ selectedItems }
      openFilter={ openFilter }
      collectionSearchValue={ collectionSearchValue }
      selectedSort={ selectedSort.label }
      isLoading={ loading || loadingCollectionV2 || loadingCollection }
      offset={ offset }
      totalPage={ totalPage }
      totalCount={ collectionDataV2?.collectionBySlugV2?.items?.filteredCount || 0 }
      onChangeCollectionSearch={ onChangeCollectionSearch }
      onChangeCheckbox={ onChangeCheckbox }
      setCounterCount={ setCounterCount }
      onChangeSearch={ onChangeSearch }
      onClickFilter={ onClickFilter }
      onClickMenu={ onClickMenu }
      onClickClearAll={ onClickClearFilter }
      onClickRemoveItem={ onClickRemoveItem }
      handlePagination={ handlePagination }
      onClickDone={ onClickDone }
    />
  );
};

// export async function getStaticPaths(context: any) {
//     return { paths: [], fallback: true };
// }
// This function gets called at build time on server-side.
// It won't be called on client-side, so you can even do
// direct database queries.
// export async function getStaticProps(params: any) {
//   return {
//     props: {},
//   };
// }

export default AboutProject;
